import React, { useRef, useState } from 'react';
import { useDispatch } from 'react-redux';
import { useTranslation } from 'react-i18next';
import { locationStepOperations } from '../../../ducks/location';
import { ComboLocation } from './ComboLocation';
import {
  Container,
  InputTitle,
  ErrorMessage,
  AddressContainer,
  Address,
  PlacesSuggestions,
  LocationCombosContainer,
  AddressFieldsContainer,
  StyledInput,
  InputWrapper
} from './styles';
import { showSubDistrict, selectsConfigByPortal } from './utils';
const site = process.env.REACT_APP_BUILD_SITE;

const LocationCombosCommon = ({
  errors,
  getValues,
  setGetSuggestions,
  setAddressValidation,
  setAddressValue,
  setValue,
  handleSelect,
  handleOnError,
  predictions,
  placesLoading,
  placesReady,
  onPlacesSearch,
  addressValue,
  addressValidation,
  handleSelectSuggestions,
  register,
  postalCode,
  getSuggestions,
  hasCoordinates,
  address,
  isMobile,
  provinceData,
  cityData,
  districtData,
  subDistrictData,
  selectedProvince,
  selectedCity,
  selectedDistrict,
  selectedSubDistrict,
  siteCodes,
  handleLocationChange
}) => {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const { zoneConfig, subZoneConfig, postalCodeConfig } = selectsConfigByPortal[site] || selectsConfigByPortal.DEFAULT;
  const [isAddressFocused, setIsAddressFocused] = useState(false);
  const [activeIndex, setActiveIndex] = useState(null);
  const skipBlurRef = useRef(false);

  return (
    <Container data-publi='locationStep-locationSection-container'>
      <AddressFieldsContainer>
        <AddressContainer>
          <InputTitle data-publi='locationStep-locationSection-address-title' margin='0px 0px 5px'>
            {t('professionalPubli.location.address')}
          </InputTitle>
          <div data-publi='locationStep-locationSection-address-container'>
            <Address
              placeholder={t('professionalPubli.location.address.placeholder')}
              autoFocus={false}
              name='addressVal'
              ref={register}
              value={getValues('addressVal') ? getValues('addressVal') : addressValue}
              autoComplete='off'
              onFocus={() => setIsAddressFocused(true)}
              onBlur={() => {
                if (skipBlurRef.current) return;
                setIsAddressFocused(false);
                handleSelectSuggestions(getValues('addressVal'));
              }}
              onChange={e => {
                const value = e.target.value;
                setAddressValidation(null);
                setGetSuggestions(true);
                setAddressValue(value);
                setValue('addressVal', value);
                onPlacesSearch(value).then(({ status }) => {
                  if (status === 'ZERO_RESULTS') {
                    handleOnError(status, () => setGetSuggestions(false));
                  }
                });
              }}
              data-publi='locationStep-locationSection-address-location'
              spellcheck='false'
              hasError={errors.addressVal}
            />
            {addressValidation}
            <ErrorMessage
              data-publi='locationStep-locationSection-address-validationError'
              errors={errors}
              name='addressVal'
              as='div'
            >
              {errors.addressVal && <p>{t('professionalPubli.location.address.error')}</p>}
            </ErrorMessage>
            {placesReady && isAddressFocused && getSuggestions && predictions.length > 0 && (
              <PlacesSuggestions data-publi='location-address-suggestion'>
                {placesLoading && (
                  <div data-publi='locationStep-locationSection-suggestion-loading' className='suggestion-item'>
                    loading....
                  </div>
                )}
                {predictions.map((prediction, index) => {
                  const className = activeIndex === index ? 'suggestion-item active' : 'suggestion-item';
                  return (
                    <div
                      data-publi={`location-suggestion-${index}`}
                      className={className}
                      key={`${prediction.place_id}-${index}`}
                      onMouseEnter={() => setActiveIndex(index)}
                      onMouseLeave={() => setActiveIndex(null)}
                      onMouseDown={() => {
                        skipBlurRef.current = true;
                        handleSelect(prediction.description, prediction.place_id, prediction, false);
                        setIsAddressFocused(false);
                        setTimeout(() => {
                          skipBlurRef.current = false;
                        }, 0);
                      }}
                      onTouchStart={() => {
                        skipBlurRef.current = true;
                        handleSelect(prediction.description, prediction.place_id, prediction, false);
                        setIsAddressFocused(false);
                        setTimeout(() => {
                          skipBlurRef.current = false;
                        }, 0);
                      }}
                    >
                      {prediction.description}
                    </div>
                  );
                })}
              </PlacesSuggestions>
            )}
          </div>
        </AddressContainer>
      </AddressFieldsContainer>
      <input
        type='hidden'
        data-publi='locationStep-location-address-coord'
        name='coordinates'
        ref={register}
        value={[hasCoordinates.lat, hasCoordinates.lng]}
      />
      <LocationCombosContainer isMobile={isMobile}>
        <ComboLocation
          data-publi='locationStep-locationSection-combo-province'
          isMobile={isMobile}
          title={t('professionalPubli.location.province')}
          onChange={e => handleLocationChange(e, 'province')}
          value={selectedProvince && selectedProvince.location_id}
          disabled={!address}
          id='provinceSelect'
          placeholder={t('professionalPubli.location.province.placeholder')}
          filterPlaceholder={t('professionalPubli.location.province.filterPlaceholder')}
          locationData={provinceData}
          errors={errors.provinceVal}
        />
        <ComboLocation
          data-publi='locationStep-locationSection-combo-city'
          isMobile={isMobile}
          title={t('professionalPubli.location.city')}
          onChange={e => handleLocationChange(e, 'city')}
          value={selectedCity && selectedCity.location_id}
          disabled={!selectedProvince || !selectedProvince.location_id}
          id='citySelect'
          placeholder={t('professionalPubli.location.city.placeholder')}
          filterPlaceholder={t('professionalPubli.location.city.filterPlaceholder')}
          locationData={cityData}
          errors={!errors.provinceVal && errors.cityVal}
        />
        {(districtData?.length > 0 || zoneConfig.mandatory) &&
          zoneConfig.visible &&
          (districtData?.length > 0 || !districtData) && (
            <ComboLocation
              data-publi='locationStep-locationSection-combo-zone'
              isMobile={isMobile}
              title={t('professionalPubli.location.zone')}
              onChange={e => handleLocationChange(e, 'district')}
              value={selectedDistrict && selectedDistrict.location_id}
              disabled={!selectedCity || districtData?.length === 0}
              id='districtSelect'
              placeholder={t('professionalPubli.location.zone.placeholder')}
              filterPlaceholder={t('professionalPubli.location.zone.filterPlaceholder')}
              locationData={districtData}
              errors={errors.districtVal}
            />
          )}
        {((showSubDistrict && subDistrictData.length !== 0) || subZoneConfig.mandatory) && subZoneConfig.visible && (
          <ComboLocation
            data-publi='locationStep-locationSection-combo-subzone'
            isMobile={isMobile}
            title={t('professionalPubli.location.subzone')}
            onChange={e => handleLocationChange(e, 'subdistrict')}
            value={selectedSubDistrict && selectedSubDistrict.location_id}
            disabled={!subDistrictData || subDistrictData.length === 0}
            id='subDistrictSelect'
            placeholder={t('professionalPubli.location.subzone.placeholder')}
            filterPlaceholder={t('professionalPubli.location.subzone.filterPlaceholder')}
            locationData={subDistrictData}
            errors={errors.subDistrictVal}
          />
        )}
        {postalCodeConfig.visible && selectedCity && (
          <InputWrapper isMobile={isMobile} data-publi='locationStep-locationSection-complement-postalcode-wrapper'>
            <InputTitle data-publi='locationStep-locationSection-complement-postalcode'>
              {t('professionalPubli.location.postalCode')}
            </InputTitle>
            <StyledInput
              name={'postalCode'}
              ref={register}
              value={postalCode}
              onChange={e => {
                dispatch(locationStepOperations.setPostalCode(e.target.value));
              }}
              placeholder={t('professionalPubli.location.postalCode.placeholder')}
            />
            <ErrorMessage
              data-publi='locationStep-locationSection-postalCode-validationError'
              errors={errors}
              name='postalCode'
              as='div'
            >
              {errors.postalCode && <p>{t('professionalPubli.location.postalCode.error')}</p>}
            </ErrorMessage>
          </InputWrapper>
        )}
      </LocationCombosContainer>
    </Container>
  );
};

export default LocationCombosCommon;
